import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import {serializeFilters} from 'nutshell-core';

import {Routing} from 'nutshell-core/routing';
import type {CoreEntityType, FilterObject} from 'nutshell-core/types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {ModalBody} from 'shells/typography/modal-body';

import type {
    FilteredListAddToDripSequence as FilteredListAddToDripSequenceMutation,
    FilteredListAddToDripSequenceVariables as FilteredListAddToDripSequenceMutationVariables,
    EditionSendToFilteredList as EditionSendToFilteredListMutation,
    EditionSendToFilteredListVariables as EditionSendToFilteredListMutationVariables,
} from 'nutshell-graphql-types';

import type {SelectionQuery} from '../bulk-email/bulk-email-modals/bulk-personal-email-modal';

import EDITION_SEND_TO_FILTERED_LIST from './graphql/mutations/edition-send-to-filtered-list.graphql';
import FILTERED_LIST_ADD_TO_DRIP_SEQUENCE from './graphql/mutations/filtered-list-add-to-drip-sequence.graphql';
import {ReadyForSendingMessagePreview} from './ready-for-sending-message-preview';
import {ReadyForSendingPageFooter} from './ready-for-sending-page-footer';
import {
    getGraphqlEntityTypeFromCoreEntityType,
    getHumanReadableEntityType,
    getHumanReadableOutreachItemType,
    getOutreachItemType,
} from './send-marketing-outreach-helpers';
import type {OutreachItem, SentOutreachItem} from './send-marketing-outreach-modal';

import styles from './ready-for-sending-page.module.css';

type Props = {
    outreachItem: OutreachItem;
    onClickBack: () => void;
    onOutreachSendSuccess: (sentOutreach: SentOutreachItem) => void;
    numRecipients: number;
    entityType: CoreEntityType;
    query: SelectionQuery;
    isMassSelect: boolean;
    omittedItems: string[];
    selectedItems: string[];
};

export function ReadyForSendingPage(props: Props) {
    const {
        outreachItem,
        onClickBack,
        onOutreachSendSuccess,
        numRecipients,
        entityType,
        query,
        isMassSelect,
        omittedItems,
        selectedItems,
    } = props;

    const outreachItemType = getOutreachItemType(outreachItem);
    const outreachItemTypeReadable = getHumanReadableOutreachItemType(outreachItem);
    const isDraft = outreachItem.status === 'NEW';

    // Only checking if already sent for non-drafts via people list only
    const shouldCheckForAlreadyReceived = !isDraft && entityType === 'contacts';

    const [isLoadingAlreadyReceived, setIsLoadingAlreadyReceived] = React.useState<boolean>(
        shouldCheckForAlreadyReceived
    );
    const [errorLoadingAlreadyReceived, setErrorLoadingAlreadyReceived] =
        React.useState<boolean>(false);
    const [numAlreadyReceived, setNumAlreadyReceived] = React.useState<number | null | undefined>();

    const [isSendingOutreachItem, setIsSendingOutreachItem] = React.useState<boolean>(false);
    const [errorSendingOutreachItem, setErrorSendingOutreachItem] = React.useState<
        string | null | undefined
    >();

    // Mutation for adding to drip sequence
    const [filteredListAddToDripSequence] = useMutation<
        FilteredListAddToDripSequenceMutation,
        FilteredListAddToDripSequenceMutationVariables
    >(FILTERED_LIST_ADD_TO_DRIP_SEQUENCE);

    // Mutation for sending edition
    const [editionSendToFilteredList] = useMutation<
        EditionSendToFilteredListMutation,
        EditionSendToFilteredListMutationVariables
    >(EDITION_SEND_TO_FILTERED_LIST);

    function sendOutreachItemMutation(
        shouldIncludeAlreadyReceived: boolean,
        isDraftEdition?: boolean,
        editionName?: string
    ): Promise<any> {
        // Some of the input is the same for everything
        const sharedInput = {
            filterString: getDecodedFilterString(query, isMassSelect, selectedItems, omittedItems),
            entityType: getGraphqlEntityTypeFromCoreEntityType(entityType),
            includePreviousRecipients: shouldIncludeAlreadyReceived,
        } as const;

        // Mutation to use depends on the outreach item
        if (isDraftEdition) {
            // Use edition mutation
            return editionSendToFilteredList({
                variables: {
                    input: {
                        editionId: outreachItem.id,
                        ...sharedInput,
                    },
                },
            });
        } else if (editionName) {
            // Use edition mutation
            return editionSendToFilteredList({
                variables: {
                    input: {
                        editionId: outreachItem.id,
                        editionName: editionName,
                        ...sharedInput,
                    },
                },
            });
        } else {
            // It is a drip sequence
            return filteredListAddToDripSequence({
                variables: {
                    input: {
                        dripSequenceId: outreachItem.id,
                        ...sharedInput,
                    },
                },
            });
        }
    }

    function onClickConfirmSend(
        shouldIncludeAlreadyReceived: boolean,
        isDraftEdition?: boolean,
        editionName?: string
    ) {
        // Set that we are sending the outreach item
        setIsSendingOutreachItem(true);

        sendOutreachItemMutation(shouldIncludeAlreadyReceived, isDraftEdition, editionName)
            .then((mutationResult) => {
                setIsSendingOutreachItem(false);
                if (
                    mutationResult &&
                    mutationResult.data &&
                    mutationResult.data.filteredListAddToDripSequence &&
                    mutationResult.data.filteredListAddToDripSequence.dripSequence
                ) {
                    const dripSequence =
                        mutationResult.data.filteredListAddToDripSequence.dripSequence;

                    if (
                        typeof dripSequence.name === 'string' &&
                        typeof dripSequence.htmlUrlPath === 'string'
                    ) {
                        onOutreachSendSuccess({
                            outreachType: 'DripSequence',
                            name: dripSequence.name,
                            htmlUrlPath: dripSequence.htmlUrlPath,
                        });
                    }
                } else if (
                    mutationResult &&
                    mutationResult.data &&
                    mutationResult.data.editionSendToFilteredList &&
                    mutationResult.data.editionSendToFilteredList.edition
                ) {
                    const edition = mutationResult.data.editionSendToFilteredList.edition;

                    if (
                        edition.campaign &&
                        typeof edition.name === 'string' &&
                        typeof edition.htmlUrlPath === 'string'
                    ) {
                        onOutreachSendSuccess({
                            outreachType:
                                edition.campaign.type === 'BROADCAST' ? 'Broadcast' : 'Newsletter',
                            name: typeof edition.name === 'string' ? edition.name : '',
                            htmlUrlPath:
                                typeof edition.htmlUrlPath === 'string' ? edition.htmlUrlPath : '',
                            sentFromDraft: isDraft,
                        });
                    }
                }
            })
            .catch((error) => {
                setErrorSendingOutreachItem(safelyGetGraphQLErrorMessage(error));
                setIsSendingOutreachItem(false);
            });
    }

    // Currently only checking the number of already received for contacts
    if (shouldCheckForAlreadyReceived) {
        // Get the already filters for getting those who have already received the outreach item
        const alreadyReceivedFilters = getAlreadyReceivedFilters(outreachItem.id, outreachItemType);
        const filterString = getDecodedFilterString(
            query,
            isMassSelect,
            selectedItems,
            omittedItems,
            alreadyReceivedFilters
        );

        $.ajax({
            url: `/rest/${entityType}/list?summary=true&${filterString}`,
            type: 'GET',
            dataType: 'json',
            contentType: 'application/json',
        })
            .then((response) => {
                const {count} = response;

                // If count is not 0, then some recipients have already received the message
                if (count) {
                    setNumAlreadyReceived(count);
                }

                setIsLoadingAlreadyReceived(false);
            })
            .fail(() => {
                setErrorLoadingAlreadyReceived(true);
                setIsLoadingAlreadyReceived(false);
            });
    }

    const entityTypeSingular = getHumanReadableEntityType(entityType);
    const entityTypePlural = getHumanReadableEntityType(entityType, true);

    return (
        <div className={styles['ready-for-sending-page']}>
            <ReadyForSendingPageHeading
                numRecipients={numRecipients}
                outreachItemType={outreachItemType}
                isDraft={isDraft}
            />
            <ReadyForSendingMessagePreview
                outreachItem={outreachItem}
                outreachItemType={outreachItemType}
                outreachItemTypeReadable={outreachItemTypeReadable}
                isLoading={isLoadingAlreadyReceived}
                error={errorLoadingAlreadyReceived}
                recipientEntityType={
                    numAlreadyReceived === 1 ? entityTypeSingular : entityTypePlural
                }
                numRecipients={numRecipients}
                numAlreadyReceived={numAlreadyReceived}
            />
            <ReadyForSendingPageFooter
                onClickBack={onClickBack}
                onClickConfirmSend={onClickConfirmSend}
                isSending={isSendingOutreachItem}
                errorSending={errorSendingOutreachItem ? errorSendingOutreachItem : undefined}
                clearErrorSending={() => setErrorSendingOutreachItem(undefined)}
                numAlreadyReceived={numAlreadyReceived}
                numRecipients={numRecipients}
                outreachItemType={outreachItemType}
                entityTypeNoun={numRecipients === 1 ? entityTypeSingular : entityTypePlural}
                isDraft={isDraft}
                outreachName={outreachItem.name}
            />
        </div>
    );
}

function getDecodedFilterString(
    query: SelectionQuery,
    isMassSelect: boolean,
    selectedItems: string[],
    omittedItems: string[],
    alreadyReceivedFilters?: FilterObject[]
): string {
    const params: Record<string, any> = {};
    const filters = [];

    // Including already received filter if this is passed in
    if (alreadyReceivedFilters) {
        filters.push(...alreadyReceivedFilters);
    }

    if (isMassSelect) {
        // If there are applied filters add them to the list of filters
        if (query.filter) {
            filters.push(...query.filter.selected);
        }
        // If search has been applied add that to the params
        if (query.search && query.search.selected) {
            params.q = query.search.selected;
        }
        // If there are omitted items
        if (omittedItems.length) {
            params.omittedItems = omittedItems.join(',');
        }
    } else if (selectedItems.length) {
        // The list of selected ids needs to be added as a filter on id
        filters.push({id: selectedItems.join(',')});
    }

    // Serialize all filters - already received + applied (if any)
    params.filter = serializeFilters(filters);

    return decodeURI(Routing.param(params));
}

function getAlreadyReceivedFilters(outreachId: string, outreachItemType: string): FilterObject[] {
    if (outreachItemType === 'DRIP_SEQUENCE') {
        return [
            {
                inProgressDripSequences: {
                    anyAll: 'any',
                    data: [{data: outreachId, isNot: 'true'}],
                    none: 'false',
                },
            },
            {
                receivedDripSequences: {
                    anyAll: 'any',
                    data: [{data: outreachId}],
                },
            },
        ];
    } else {
        return [
            {
                receivedEditions: {
                    anyAll: 'any',
                    data: [{data: outreachId}],
                    none: 'false',
                },
            },
        ];
    }
}

const ReadyForSendingPageHeading = (props: {
    numRecipients: number;
    outreachItemType: string;
    isDraft: boolean;
}) => {
    const {numRecipients, outreachItemType, isDraft} = props;

    let pageHeaderText = `You’ve selected ${numRecipients} recipient${
        numRecipients === 1 ? '' : 's'
    }`;
    let pageSubheaderText =
        'This action cannot be undone, so double check that everything looks correct before proceeding';

    switch (outreachItemType) {
        case 'BROADCAST':
            pageHeaderText = pageHeaderText.concat(
                isDraft ? ' for this broadcast' : ' to receive a new copy of this broadcast'
            );
            break;
        case 'NEWSLETTER':
            pageHeaderText = pageHeaderText.concat(
                isDraft ? ' for this edition' : ' to receive a new copy of this edition'
            );
            break;
        case 'DRIP_SEQUENCE':
            pageHeaderText = pageHeaderText.concat(' to be added to this drip sequence');
            pageSubheaderText =
                'Double check that everything looks correct before proceeding, but you can always remove recipients from a drip’s queue';
            break;
    }

    return (
        <div>
            <ModalBody>
                <div className={styles['dark-text']}>{pageHeaderText}</div>
            </ModalBody>
            <div>{pageSubheaderText}</div>
        </div>
    );
};
